<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview :title="pageTitle">
        <template v-slot:preview>
          <div v-if="loading" class="text-center">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="ml-2">Đang tải dữ liệu...</strong>
          </div>

          <div v-else>
            <!-- Form Mode (Add/Edit) -->
            <ValidationObserver ref="observer">
              <b-form @submit.prevent="onSubmit">
                <!-- Customer Information Component -->
                <CustomerInformation
                  :lead-form="leadForm"
                  :mode="mode"
                  :title-options="titleOptions"
                  :gender-options="genderOptions"
                  :source-options="sourceOptions"
                  :status-options="statusOptions"
                  :customer-group-options="customerGroupOptions"
                  :evaluation-options="evaluationOptions"
                  @update-field="updateField"
                />

                <!-- Interested Product Component -->
                <InterestedProduct
                  :lead-form="leadForm"
                  :mode="mode"
                  :selected-products="selectedProducts"
                  @update-field="updateField"
                  @products-updated="onProductsUpdated"
                />

                <!-- Management Information Component -->
                <ManagementInformation
                  :lead-form="leadForm"
                  :mode="mode"
                  @update-field="updateField"
                />

                <!-- Action buttons -->
                <div class="d-flex mt-5">
                  <b-button
                    variant="secondary"
                    class="mr-3"
                    @click="$router.push({ name: 'list-leads' })"
                  >
                    Hủy
                  </b-button>
                  <b-button
                    variant="primary"
                    type="submit"
                    v-show="mode !== MODE.VIEW"
                    :disabled="submitting"
                  >
                    <b-spinner v-if="submitting" small class="mr-2"></b-spinner>
                    {{ mode === MODE.ADD ? 'Lưu' : 'Cập nhật' }}
                  </b-button>
                </div>
              </b-form>
            </ValidationObserver>
          </div>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import LeadService from '@/core/api/lead/lead.service';
import CustomerInformation from './components/CustomerInformation.vue';
import InterestedProduct from './components/InterestedProduct.vue';
import ManagementInformation from './components/ManagementInformation.vue';

import { makeToastFaile, makeToastSuccess, convertPrice } from '@/utils/common';
import { DATE_FORMAT, formatDBDate, formatDate } from '@/utils/date';

import { ValidationObserver, extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';

import { permissionMixin } from '@/view/mixins/permission.mixins';

import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { LeadModel } from '@/core/models';

import * as ENUM_NAME from '@/utils/enum-name';
import * as ENUM from '@/utils/enum';

extend('required', {
  ...required,
  message: `Vui lòng nhập`,
});

const ROUTE_CONFIGS = {
  'add-lead': {
    mode: ENUM.MODE.ADD,
    title: 'Thêm mới Lead',
  },
  'edit-lead': {
    mode: ENUM.MODE.EDIT,
    title: 'Cập nhật Lead',
  },
  'view-lead': {
    mode: ENUM.MODE.VIEW,
    title: 'Chi tiết Lead',
  },
};

export default {
  name: 'UpsertLead',
  mixins: [permissionMixin],
  components: {
    KTCodePreview,
    ValidationObserver,
    CustomerInformation,
    InterestedProduct,
    ManagementInformation,
  },
  data() {
    return {
      // Enums
      MODE: ENUM.MODE,
      PERMISSIONS: ENUM.PERMISSIONS,

      //   State
      loading: false,
      submitting: false,

      //   Form
      leadForm: new LeadModel(),

      //   Options
      selectedProducts: [], // Track selected products separately
      titleOptions: this.getOptions(ENUM.LEAD_TITLE, ENUM_NAME.LEAD_TITLE_NAME),
      genderOptions: this.getOptions(ENUM.GENDER, ENUM_NAME.GENDER_NAME),
      sourceOptions: this.getOptions(
        ENUM.LEAD_SOURCE,
        ENUM_NAME.LEAD_SOURCE_NAME,
      ),
      statusOptions: this.getOptions(
        ENUM.LEAD_STATUS,
        ENUM_NAME.LEAD_STATUS_NAME,
      ),
      customerGroupOptions: this.getOptions(
        ENUM.CUSTOMER_GROUP,
        ENUM_NAME.CUSTOMER_GROUP_NAME,
      ),
      evaluationOptions: this.getOptions(
        ENUM.LEAD_EVALUATION,
        ENUM_NAME.LEAD_EVALUATION_NAME,
      ),
    };
  },
  computed: {
    mode() {
      const routeName = this.$route.name;
      return ROUTE_CONFIGS[routeName]?.mode || this.MODE.ADD;
    },
    pageTitle() {
      const routeName = this.$route.name;
      return ROUTE_CONFIGS[routeName]?.title || 'Lead';
    },
    isFormValid() {
      return !!(
        this.leadForm.fullName &&
        this.leadForm.title &&
        this.leadForm.gender &&
        this.leadForm.phoneNumber
      );
    },
  },

  async mounted() {
    await this.setBreadCrumbs();

    this.leadForm.id = this.$route.params.id;

    if (this.mode !== this.MODE.ADD && this.leadForm.id) {
      await this.loadLead();
    }
  },
  methods: {
    convertPrice,

    async setBreadCrumbs() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Leads', route: '/crm/leads' },
        { title: this.pageTitle, active: true },
      ]);
    },

    updateField({ field, value }) {
      console.log('Updating field:', field, 'with value:', value);
      this.$set(this.leadForm, field, value);
    },

    onProductsUpdated(updatedProducts) {
      this.selectedProducts = updatedProducts;

      // Update leadForm for API submission
      this.leadForm.interestedProducts = updatedProducts.map((product) => ({
        productId: product.id,
        interestStatus:
          product.interestStatus || ENUM.PRODUCT_INTEREST_STATUS.INTERESTED,
      }));
    },

    initializeSelectedProducts() {
      if (this.leadForm?.leadProducts?.length > 0) {
        this.selectedProducts = this.leadForm.leadProducts.map((lp) => ({
          id: lp.product.id,
          name: lp.product.name,
          code: lp.product.code,
          sellingPrice: lp.product.sellingPrice,
          quantityInStock: lp.product.quantityInStock || 0,
          interestStatus: lp.interestStatus,
        }));
      } else {
        this.selectedProducts = [];
      }
    },

    async loadLead() {
      this.loading = true;
      try {
        const response = await LeadService.getById(this.leadForm.id);
        const leadData = response.data.data;

        this.leadForm = LeadModel.fromJson({
          ...leadData,
          birthDate: leadData.birthDate
            ? formatDate(leadData.birthDate, DATE_FORMAT)
            : '',
          issueDate: leadData.issueDate
            ? formatDate(leadData.issueDate, DATE_FORMAT)
            : '',
          conversionDate: leadData.conversionDate
            ? formatDate(leadData.conversionDate, DATE_FORMAT)
            : '',
          citizenIdIssuedDate: leadData.citizenIdIssuedDate
            ? formatDate(leadData.citizenIdIssuedDate, DATE_FORMAT)
            : '',
        });

        this.initializeSelectedProducts();
      } catch (error) {
        console.error('Error loading lead:', error);
        makeToastFaile('Không thể tải thông tin lead');
        this.$router.push({
          name: 'list-leads',
        });
      } finally {
        this.loading = false;
      }
    },
    async onSubmit() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        makeToastFaile('Vui lòng kiểm tra lại thông tin nhập');
        return;
      }

      this.submitting = true;
      try {
        const submitData = {
          ...this.leadForm,
          source: this.leadForm.source,
          birthDate: formatDBDate(this.leadForm.birthDate),
          issueDate: formatDBDate(this.leadForm.issueDate),
          conversionDate: formatDBDate(this.leadForm.conversionDate),
          citizenIdIssuedDate: formatDBDate(this.leadForm.citizenIdIssuedDate),
        };

        console.log('Submitting lead data:', submitData);

        const submitConfigs = {
          [this.MODE.ADD]: {
            service: () => LeadService.create(submitData),
            message: 'Thêm mới lead thành công',
          },
          [this.MODE.EDIT]: {
            service: () => LeadService.update(submitData.id, submitData),
            message: 'Cập nhật lead thành công',
          },
        };

        const config = submitConfigs[this.mode];
        if (!config) {
          return makeToastFaile('Không thể thêm mới/cập nhật Lead');
        }

        const { service, message } = config;

        await service();
        makeToastSuccess(message);

        this.$router.push({
          name: 'list-leads',
        });
      } catch (error) {
        console.error('Error saving lead:', error);
        makeToastFaile(
          `Có lỗi xảy ra khi ${
            this.mode === this.MODE.ADD ? 'thêm mới' : 'cập nhật'
          } lead`,
        );
      } finally {
        this.submitting = false;
      }
    },

    getOptions(enumValues, enumNames) {
      if (!enumValues || !enumNames) return [];
      if (!Object.values(enumValues).length) return [];

      const options = Object.values(enumValues).map((val) => ({
        value: val,
        text: enumNames[val] || val,
      }));

      return options;
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('vi-VN');
    },
    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('vi-VN');
    },

    getStatusClass(status) {
      const statusMap = {
        [ENUM.LEAD_STATUS.INTERESTED]: 'label-light-primary',
        [ENUM.LEAD_STATUS.NOT_INTERESTED]: 'label-light-danger',
        [ENUM.LEAD_STATUS.POTENTIAL]: 'label-light-warning',
        [ENUM.LEAD_STATUS.PURCHASED]: 'label-light-success',
      };
      return statusMap[status] || 'label-light-secondary';
    },
    getProductName(productId) {
      if (!productId || !this.products.length) return '';
      const product = this.products.find((p) => p.id == productId);
      return product ? product.name : '';
    },
  },
};
</script>

<style scoped>
.label {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
}
</style>
